import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePoints } from '../hooks/usePoints';
import StreamerHeader from '../components/StreamerHeader';
import { apiClient, EventSchedule } from '../lib/api-client';
import { ROUTES } from '../lib/constants';

export default function Console() {
    const { user, logout, isViewer } = useAuth();
    const { points, isLoading: pointsLoading } = usePoints();
    const navigate = useNavigate();
    const { i18n } = useTranslation();

    // Force Japanese language for streamer pages (non-viewers)
    useEffect(() => {
        if (!isViewer() && i18n.language !== 'ja') {
            i18n.changeLanguage('ja');
        }
    }, [i18n, isViewer]);

    // 配信者用のスケジュール状態
    const [liveSchedules, setLiveSchedules] = useState<EventSchedule[]>([]);
    const [upcomingSchedules, setUpcomingSchedules] = useState<EventSchedule[]>([]);
    const [suspendedSchedules, setSuspendedSchedules] = useState<EventSchedule[]>([]);
    const [pastSchedules, setPastSchedules] = useState<EventSchedule[]>([]);
    const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
    const [schedulesError, setSchedulesError] = useState('');

    // 配信者の場合、自分のスケジュールを取得
    useEffect(() => {
        if (!isViewer() && user?.id) {
            loadMySchedules();
        }
    }, [user?.id]);

    const loadMySchedules = async () => {
        if (!user?.id) return;

        setIsLoadingSchedules(true);
        setSchedulesError('');
        try {
            // 配信中のスケジュールを取得
            const liveResponse = await apiClient.listEventSchedules({
                streamer_id: user.id,
                status_filter: 'live',
            });
            setLiveSchedules(liveResponse.schedules);

            // 予定のスケジュールを取得
            const upcomingResponse = await apiClient.listEventSchedules({
                streamer_id: user.id,
                status_filter: 'scheduled',
            });
            setUpcomingSchedules(upcomingResponse.schedules);

            // 中断中のスケジュールを取得（再開可能）- suspendedとpausedの両方
            const suspendedResponse = await apiClient.listEventSchedules({
                streamer_id: user.id,
                status_filter: 'suspended',
            });
            const pausedResponse = await apiClient.listEventSchedules({
                streamer_id: user.id,
                status_filter: 'paused',
            });
            // suspendedとpausedを統合（両方とも再開可能）
            setSuspendedSchedules([...suspendedResponse.schedules, ...pausedResponse.schedules]);

            // 完了したスケジュールを取得（最新3件）
            const completedResponse = await apiClient.listEventSchedules({
                streamer_id: user.id,
                status_filter: 'completed',
            });
            // 最新3件のみ保持
            setPastSchedules(completedResponse.schedules.slice(0, 3));
        } catch (err) {
            setSchedulesError(err instanceof Error ? err.message : 'スケジュールの取得に失敗しました');
        } finally {
            setIsLoadingSchedules(false);
        }
    };

    const handleLogout = async () => {
        // Check role BEFORE logout (logout sets user to null)
        const wasViewer = isViewer();
        const targetPath = wasViewer ? ROUTES.LOGIN : ROUTES.STAFF_LOGIN;

        // For streamers, pause any active streams before logout (not stop)
        // This allows resuming within 1 minute grace period
        if (!wasViewer && user?.id) {
            try {
                const response = await apiClient.getActiveStreams();
                const myStreams = response.streams.filter(s => s.user_id === user.id);
                for (const stream of myStreams) {
                    await apiClient.pauseStream(stream.stream_key);
                }
            } catch (err) {
                console.error('Failed to pause streams on logout:', err);
            }
        }

        logout();
        window.location.href = targetPath;
    };

    const formatScheduleTime = (isoString: string) => {
        const date = new Date(isoString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${month}/${day} ${hours}:${minutes}`;
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'scheduled': return '予定';
            case 'live': return '配信中';
            case 'suspended': return '一時中断中';
            case 'completed': return '完了';
            case 'cancelled': return 'キャンセル';
            default: return status;
        }
    };

    const handleStartBroadcast = (scheduleId: string) => {
        // スケジュールIDをクエリパラメータとして渡して配信ページへ遷移
        navigate(`${ROUTES.PUBLISH_OME}?schedule=${scheduleId}`);
    };

    // 配信者（ADMIN/STREAMER）の場合はStreamerHeaderを使用
    if (!isViewer()) {
        return (
            <div className="min-h-screen bg-gray-100">
                <StreamerHeader
                    activeTab="studio"
                    onMyPageClick={() => navigate(ROUTES.STREAMER_MYPAGE)}
                    onStudioClick={() => {
                        // Already on studio
                    }}
                    onLogout={handleLogout}
                    username={user?.username}
                />

                {/* Main Content */}
                <div className="container mx-auto px-4 py-8">
                    {/* Welcome Section */}
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-800">
                            ようこそ Live Chat へ
                        </h2>
                    </div>

                    {/* Schedule Selection Section */}
                    <div className="max-w-3xl mx-auto mb-8">
                        <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-200">
                            <div className="flex items-center mb-4">
                                <span className="text-3xl mr-3">📅</span>
                                <h2 className="text-2xl font-bold text-primary">
                                    配信スケジュール
                                </h2>
                            </div>

                            {isLoadingSchedules ? (
                                <p className="text-gray-500 text-center py-4">読み込み中...</p>
                            ) : schedulesError ? (
                                <p className="text-red-500 text-center py-4">{schedulesError}</p>
                            ) : liveSchedules.length === 0 && upcomingSchedules.length === 0 && suspendedSchedules.length === 0 && pastSchedules.length === 0 ? (
                                <div className="text-center py-6">
                                    <p className="text-gray-500 mb-4">
                                        現在予定されている配信スケジュールはありません
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        管理者またはマネージャーにスケジュール登録を依頼してください
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* 配信中のスケジュール（最優先表示） */}
                                    {liveSchedules.length > 0 && (
                                        <div className="space-y-3">
                                            <h3 className="text-sm font-semibold text-red-600 border-b border-red-300 pb-2 flex items-center gap-2">
                                                <span className="animate-pulse">🔴</span>
                                                配信中
                                            </h3>
                                            {liveSchedules.map((schedule) => (
                                                <div
                                                    key={schedule.id}
                                                    className="flex items-center justify-between p-4 bg-red-50 rounded-lg border-2 border-red-400 hover:bg-red-100 transition-colors"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-800">
                                                            {formatScheduleTime(schedule.scheduled_start)} 〜 {formatScheduleTime(schedule.scheduled_end)}
                                                        </div>
                                                        <div className="text-sm text-red-700 font-medium">
                                                            🎥 現在配信中です
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleStartBroadcast(schedule.id)}
                                                        className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-semibold"
                                                    >
                                                        配信画面を開く
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* 中断中のスケジュール（優先表示・再開可能） */}
                                    {suspendedSchedules.length > 0 && (
                                        <div className="space-y-3">
                                            <h3 className="text-sm font-semibold text-yellow-600 border-b border-yellow-300 pb-2 flex items-center gap-2">
                                                <span className="animate-pulse">⚠️</span>
                                                一時中断中（再開可能）
                                            </h3>
                                            {suspendedSchedules.map((schedule) => (
                                                <div
                                                    key={schedule.id}
                                                    className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border-2 border-yellow-400 hover:bg-yellow-100 transition-colors"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-800">
                                                            {formatScheduleTime(schedule.scheduled_start)} 〜 {formatScheduleTime(schedule.scheduled_end)}
                                                        </div>
                                                        <div className="text-sm text-yellow-700 font-medium">
                                                            ⏳ 配信が中断されています。1分以内に再開してください。
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleStartBroadcast(schedule.id)}
                                                        className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors font-semibold animate-pulse"
                                                    >
                                                        配信を再開する
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* 予定のスケジュール */}
                                    {upcomingSchedules.length > 0 && (
                                        <div className="space-y-3">
                                            <h3 className="text-sm font-semibold text-gray-600 border-b pb-2">予定</h3>
                                            {upcomingSchedules.map((schedule) => (
                                                <div
                                                    key={schedule.id}
                                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-800">
                                                            {formatScheduleTime(schedule.scheduled_start)} 〜 {formatScheduleTime(schedule.scheduled_end)}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            ステータス: {getStatusLabel(schedule.status)}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleStartBroadcast(schedule.id)}
                                                        className="px-6 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg transition-colors font-semibold"
                                                    >
                                                        配信ページを開く
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* 過去のスケジュール（最新3件） */}
                                    {pastSchedules.length > 0 && (
                                        <div className="space-y-3">
                                            <h3 className="text-sm font-semibold text-gray-400 border-b pb-2">過去の配信</h3>
                                            {pastSchedules.map((schedule) => (
                                                <div
                                                    key={schedule.id}
                                                    className="flex items-center justify-between p-4 bg-gray-100 rounded-lg border border-gray-200 opacity-70"
                                                >
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-gray-600">
                                                            {formatScheduleTime(schedule.scheduled_start)} 〜 {formatScheduleTime(schedule.scheduled_end)}
                                                        </div>
                                                        <div className="text-sm text-gray-400">
                                                            ステータス: {getStatusLabel(schedule.status)}
                                                        </div>
                                                    </div>
                                                    <span className="px-4 py-2 bg-gray-300 text-gray-600 rounded-lg font-semibold">
                                                        完了
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    // ビューアーの場合は既存のヘッダーを使用
    return (
        <div className="min-h-screen bg-white">
            {/* Header Bar */}
            <header className="bg-primary-200 text-primary-900 shadow-md">
                <div className="container mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl font-bold">Live Chat</h1>
                        <div className="flex items-center gap-4">
                            <div className="text-sm">
                                <span className="opacity-90">ログイン中:</span>
                                <span className="font-semibold ml-1">{user?.username}</span>
                                <span className="ml-4 bg-accent text-white px-3 py-1 rounded-full font-semibold text-xs">
                                    💰 {pointsLoading ? '...' : `${points?.toLocaleString() || 0}pt`}
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <Link
                                    to={ROUTES.POINTS}
                                    className="bg-accent hover:bg-accent-600 text-white text-sm font-semibold py-2 px-4 rounded transition-colors"
                                >
                                    ポイント購入
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded transition-colors"
                                >
                                    ログアウト
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-8">
                {/* Welcome Section */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-800">
                        ようこそ Live Chat へ
                    </h2>
                </div>

                {/* Main Navigation Cards */}
                <div className="grid grid-cols-1 gap-6 max-w-5xl mx-auto mb-8">
                    {/* Watching Card */}
                    <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all">
                        <div className="flex items-center mb-4">
                            <span className="text-3xl mr-3">📺</span>
                            <h2 className="text-2xl font-bold text-success">
                                視聴する
                            </h2>
                        </div>
                        <p className="text-gray-600 mb-4 text-sm">
                            低遅延でライブ配信を視聴
                        </p>
                        <Link
                            to={ROUTES.WATCH}
                            className="block w-full py-3 px-6 bg-primary hover:bg-primary-600 text-white rounded-lg transition-all font-semibold text-center shadow-md"
                        >
                            視聴ページを開く →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
